'use client';

import { validateOTPAction } from '@/app/api/server-actions/otp';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, AlertTitle, Button, Input } from '@venienergygroup/shared-components';
import { ArrowLeft } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import z from 'zod';

const createCodeSchema = (t: ReturnType<typeof useTranslations>) =>
  z.object({
    code: z
      .string()
      .min(4, t('otp.code.validationMinLength'))
      .regex(/^\d+$/, t('otp.code.validationDigitsOnly')),
  });

type CodeFormValues = z.infer<ReturnType<typeof createCodeSchema>>;

type OTPCodeInputProps = { email: string; onBackClick: () => void; redirectUrl?: string };

export const OTPCodeInput = ({ email, onBackClick, redirectUrl = '/' }: OTPCodeInputProps) => {
  const t = useTranslations('LoginPage');
  const router = useRouter();
  const [serverError, setServerError] = useState<string | null>(null);

  const codeSchema = createCodeSchema(t);
  const { control, handleSubmit, formState } = useForm<CodeFormValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  });

  const { isSubmitting, errors } = formState;

  const onSubmit: SubmitHandler<CodeFormValues> = async (data) => {
    setServerError(null);

    const validateResult = await validateOTPAction({ otp: data.code, email });

    if (!validateResult.success) {
      setServerError(
        t(
          validateResult.errorType === 'session-setup'
            ? 'otp.code.errorSessionFailed'
            : 'otp.code.errorInvalid',
        ),
      );
      return;
    }

    router.push(redirectUrl);
  };

  return (
    <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4">
      <div className="rounded-md bg-veni-green-100 p-3">
        <p className="text-sm text-veni-green-800">
          {t('otp.code.description')} <strong>{email}</strong>
        </p>
      </div>

      <div className="flex flex-col gap-y-2">
        <label htmlFor="otp-code" className="block text-sm font-medium text-gray-700">
          {t('otp.code.label')}
        </label>
        <Controller
          name="code"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="otp-code"
              type="text"
              placeholder={t('otp.code.placeholder')}
              disabled={isSubmitting}
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
              className="text-center font-mono text-2xl tracking-widest"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const filtered = e.target.value.replace(/[^\d]/g, '').slice(0, 6);
                field.onChange(filtered);
              }}
            />
          )}
        />
        {errors.code && <p className="mt-1 text-sm text-error-500">{errors.code.message}</p>}
      </div>

      <div className="flex flex-col gap-y-2">
        {serverError && (
          <Alert variant="error">
            <AlertTitle>{serverError}</AlertTitle>
          </Alert>
        )}

        <Button
          type="submit"
          disabled={isSubmitting || !formState.isDirty}
          className="w-full disabled:cursor-not-allowed"
        >
          {isSubmitting ? t('otp.code.verifyButtonLoading') : t('otp.code.verifyButton')}
        </Button>

        <Button type="button" variant="ghost" onClick={onBackClick} disabled={isSubmitting}>
          <ArrowLeft /> {t('otp.code.backButton')}
        </Button>
      </div>
    </form>
  );
};
